"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { ArrowRight, Share2, Bookmark, BookmarkCheck, ChevronLeft, ChevronRight, Check, Copy, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ManifiestoEntry, manifiestoEntries, categoryColors } from "../data"
import { FloatingNav } from "@/components/ui/FloatingNavbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

interface EntryHeaderProps {
  entry: ManifiestoEntry
}

// Utility to get/set bookmarks from localStorage
function getBookmarks(): string[] {
  if (typeof window === "undefined") return []
  try {
    return JSON.parse(localStorage.getItem("holistika_bookmarks") || "[]")
  } catch {
    return []
  }
}

function setBookmarks(bookmarks: string[]) {
  if (typeof window === "undefined") return
  localStorage.setItem("holistika_bookmarks", JSON.stringify(bookmarks))
}

export function EntryHeader({ entry }: EntryHeaderProps) {
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [shareSuccess, setShareSuccess] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  
  const currentIndex = manifiestoEntries.findIndex(e => e.id === entry.id)
  const prevEntry = currentIndex > 0 ? manifiestoEntries[currentIndex - 1] : null
  const nextEntry = currentIndex < manifiestoEntries.length - 1 ? manifiestoEntries[currentIndex + 1] : null
  const colors = categoryColors[entry.category]
  
  // Check bookmark status on mount
  useEffect(() => {
    const bookmarks = getBookmarks()
    setIsBookmarked(bookmarks.includes(entry.id))
  }, [entry.id])
  
  // Native share API handler
  const handleShare = async () => {
    const shareData = {
      title: `${entry.title}: ${entry.subtitle}`,
      text: entry.description,
      url: window.location.href,
    }
    
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData)
        setShareSuccess(true)
        setTimeout(() => setShareSuccess(false), 2000)
      } catch (err) {
        // User cancelled or share failed - fallback to copy
        if ((err as Error).name !== "AbortError") {
          handleCopyLink()
        }
      }
    } else {
      // Fallback: copy link to clipboard
      handleCopyLink()
    }
  }
  
  // Copy link fallback
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = window.location.href
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    }
  }
  
  // Toggle bookmark
  const handleBookmark = () => {
    const bookmarks = getBookmarks()
    let newBookmarks: string[]
    
    if (bookmarks.includes(entry.id)) {
      newBookmarks = bookmarks.filter(id => id !== entry.id)
      setIsBookmarked(false)
    } else {
      newBookmarks = [...bookmarks, entry.id]
      setIsBookmarked(true)
    }
    
    setBookmarks(newBookmarks)
  }

  return (
    <>
      {/* Main site navigation */}
      <FloatingNav />
      
      {/* Entry-specific header
       * Mobile padding-top is reduced (pt-16) because the floating navbar
       * is now a compact pill (~56px tall) thanks to the hamburger pattern
       * (I66 P5 mobile responsive fix 2026-05-09). On desktop the navbar
       * is still ~80px so pt-20 holds.
       */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border/50 pt-16 md:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Breadcrumb Navigation */}
            <div className="flex items-center gap-4">
              <Link 
                href="/manifiesto" 
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowRight className="h-4 w-4 rotate-180" />
                <span className="hidden sm:inline">All Entries</span>
              </Link>
              
              <div className="hidden md:flex items-center gap-1 text-muted-foreground/50">
                <span>/</span>
              </div>
              
              <div className="hidden md:flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                <span className={`text-sm font-medium ${colors.text}`}>{entry.title}</span>
              </div>
            </div>

            {/* Center: Entry Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              {prevEntry && (
                <Link
                  href={`/manifiesto/${prevEntry.slug}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {prevEntry.title}
                </Link>
              )}
              
              <span className="text-muted-foreground/30 px-2">|</span>
              
              {nextEntry ? (
                <Link
                  href={`/manifiesto/${nextEntry.slug}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                >
                  {nextEntry.title}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              ) : (
                <span className="text-sm text-muted-foreground/50 px-3 py-1.5">End of Entries</span>
              )}
            </div>

            {/* Right: Actions
             * Hidden on mobile per operator UX feedback 2026-05-09 — share +
             * bookmark lack auth context without login + cluttered the
             * mobile secondary bar. Restored at sm breakpoint for tablet+.
             * (Operator wanted to preserve the "vector-focused vibe" so
             * collapsing is the right call vs. removing entirely.)
             */}
            <div className="hidden sm:flex items-center gap-2">
              {/* Share Button with Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {shareSuccess || copySuccess ? (
                      <Check className="h-4 w-4 text-accent-primary" />
                    ) : (
                      <Share2 className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline ml-2">
                      {copySuccess ? "Copied!" : shareSuccess ? "Shared!" : "Share"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={handleShare}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share via device
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCopyLink}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Bookmark Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBookmark}
                className={`transition-colors ${
                  isBookmarked 
                    ? "text-accent-primary hover:text-accent-primary/80" 
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
              >
                {isBookmarked ? (
                  <BookmarkCheck className="h-4 w-4" />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
                <span className="hidden sm:inline ml-2">
                  {isBookmarked ? "Saved" : "Save"}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}
